import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FaPhone, FaEnvelope } from 'react-icons/fa';
import Stepper, { Step } from '../Stepper';
import { startSidebarLink } from './startPageTokens';
import {
  STEP_LABELS,
  StartStepIndicatorColumn,
  StartTaskActionTile,
  StartTaskStepContent
} from './startDashboardTaskUI';
import { getStartFreePageLabel } from '../../data/startFreePages';

const cardSurface =
  'rounded-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 shadow-sm';

const SUCCESS_MANAGER = {
  name: 'Ian Riddle',
  phone: '419-690-9665',
  email: 'ianr@buildersbreakthrough.com',
  profileImage: '/riddleshot.png'
};

// Emojis rather than icons, mirroring the live Start Dashboard sidebar.
const QUICK_LINKS = [
  { id: 'meet-team', emoji: '👋', title: 'Meet The Team' },
  { id: 'success-stories', emoji: '🏆', title: 'Success Stories' },
  { id: 'cqc-home', emoji: '🏡', title: 'Sift CQC Home' },
  { id: 'ask-question', emoji: '💬', title: 'Ask a Question' }
];

/**
 * Read-only preview of the Start portal dashboard (CRM portal invite modal).
 *
 * `freePage` is the lead's `accounts/{id}.freePage`. When nothing has been unlocked for them the
 * Claim Free Resource card is hidden, matching what the lead will actually see.
 */
export default function StartDashboardPreview({
  companyName = 'Your Company',
  firstName = 'there',
  freePage = null
}) {
  const { darkMode } = useTheme();
  const [previewStep, setPreviewStep] = useState(1);

  const quickLinks = freePage
    ? [
        ...QUICK_LINKS,
        {
          id: 'free-resource',
          emoji: '🎁',
          title: 'Claim Free Resource',
          subtitle: getStartFreePageLabel(freePage)
        }
      ]
    : QUICK_LINKS;

  return (
    <div className="p-6 md:p-8">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <img src="/bba-emblem.svg" alt="BBA Logo" className="h-16 md:h-20 w-auto" />
        </div>
        <h1 className={`text-3xl md:text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Welcome to BBA,{' '}
          <span className="text-red-600 dark:text-red-400">{firstName}</span>!
        </h1>
        <p className={`text-sm md:text-base mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          We&apos;re excited you considered partnering with us.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start max-w-7xl mx-auto">
        <div className="lg:col-span-3">
          <div className={`${cardSurface} p-5`}>
            <div className="mb-4 text-center">
              <h2 className="font-semibold text-gray-800 dark:text-white text-lg">My Tasks</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Complete the steps below to get started with BBA.
              </p>
            </div>

            <div className="mb-5">
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">0 of 4 steps complete</span>
                <span
                  className={`text-sm font-bold px-2.5 py-0.5 rounded-full ${
                    darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  0%
                </span>
              </div>
              <div className="h-3 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
                <div className="h-full bg-red-500 rounded-full" style={{ width: '0%' }} />
              </div>
            </div>

            <div className="w-full pointer-events-auto">
              <Stepper
                className="w-full items-stretch justify-start p-0"
                initialStep={previewStep}
                onStepChange={setPreviewStep}
                onFinalStepCompleted={() => {}}
                backButtonText="Previous"
                nextButtonText="Next"
                stepCircleContainerClassName="!max-w-none border-0 shadow-none bg-transparent"
                contentClassName="min-h-[160px] !px-2"
                stepContainerClassName="px-2 pb-2 flex w-full items-end"
                hideCompleteButton={true}
                renderStepIndicator={({ step, currentStep, onStepClick }) => (
                  <StartStepIndicatorColumn
                    step={step}
                    currentStep={currentStep}
                    label={STEP_LABELS[step - 1]}
                    onStepClick={onStepClick}
                  />
                )}
              >
                <Step>
                  <StartTaskStepContent
                    stepNumber={1}
                    title="Business Analysis"
                    subtitle="Complete your business assessment to unlock your personalized business analysis."
                    action={
                      <StartTaskActionTile
                        preview
                        emoji="📊"
                        label="Click Here to Complete Assessment"
                      />
                    }
                  />
                </Step>
                <Step>
                  <StartTaskStepContent
                    stepNumber={2}
                    title="Sign NDA"
                    subtitle="Ensure your business information is safe and secure."
                    action={
                      <StartTaskActionTile preview emoji="✍️" label="Click Here to Sign NDA" />
                    }
                  />
                </Step>
                <Step>
                  <StartTaskStepContent
                    stepNumber={3}
                    title="Schedule Analysis Call"
                    subtitle="Book your call with our team to review your business analysis and discuss proposed implementations."
                    action={
                      <StartTaskActionTile preview emoji="📅" label="Click Here to Schedule Call" />
                    }
                  />
                </Step>
                <Step>
                  <StartTaskStepContent
                    stepNumber={4}
                    title="Choose a Program"
                    subtitle="Select the program that best fits your business needs and goals."
                    action={
                      <StartTaskActionTile preview emoji="🎓" label="Click Here to Choose Program" />
                    }
                  />
                </Step>
              </Stepper>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-1 flex flex-col gap-3">
          {/* Compact identity row — same card the live Start Dashboard renders */}
          <div className={`${cardSurface} p-4`}>
            <div className="flex items-center gap-3">
              <img
                src={SUCCESS_MANAGER.profileImage}
                alt={SUCCESS_MANAGER.name}
                className="w-10 h-10 rounded-full object-cover shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {SUCCESS_MANAGER.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Your Success Manager</p>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex flex-col gap-1.5">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                <FaPhone className="w-3 h-3 shrink-0" aria-hidden />
                {SUCCESS_MANAGER.phone}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 min-w-0">
                <FaEnvelope className="w-3 h-3 shrink-0" aria-hidden />
                <span className="truncate">{SUCCESS_MANAGER.email}</span>
              </span>
            </div>
          </div>

          {quickLinks.map((link) => (
            <div key={link.id} className={`${startSidebarLink} pointer-events-none`}>
              <span className="shrink-0 w-10 h-10 rounded-lg bg-red-50 dark:bg-red-950/40 flex items-center justify-center text-xl leading-none">
                <span aria-hidden="true">{link.emoji}</span>
              </span>
              <span className="min-w-0">
                <span className="block font-semibold text-base text-gray-800 dark:text-white">{link.title}</span>
                {link.subtitle && (
                  <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                    {link.subtitle}
                  </span>
                )}
              </span>
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
}
